import { useState, useEffect, useCallback, useRef } from 'react';
import { STORAGE_KEY } from '../constants';
import { isValidHexColor, normalizeHexColor } from '../utils';
import { debounce } from '../utils/debounce';

interface UseRecentColorsOptions {
  maxRecentColors?: number;
  initialRecentColors?: string[];
  debounceMs?: number;
}

interface UseRecentColorsReturn {
  recentColors: string[];
  addRecentColor: (color: string) => void;
  addRecentColorImmediate: (color: string) => void;
  clearRecentColors: () => void;
}

/**
 * Hook para manejar colores recientes con persistencia en localStorage
 * Incluye debounce para evitar guardar constantemente durante el arrastre del selector
 */
export const useRecentColors = ({
  maxRecentColors = 10,
  initialRecentColors = [],
  debounceMs = 500,
}: UseRecentColorsOptions = {}): UseRecentColorsReturn => {
  const [recentColors, setRecentColors] = useState<string[]>([]);
  const pendingColorRef = useRef<string | null>(null);

  // Cargar colores recientes desde localStorage al montar
  useEffect(() => {
    if (initialRecentColors.length > 0) {
      // Validar y normalizar colores iniciales
      const validColors = initialRecentColors
        .map((color) => normalizeHexColor(color))
        .filter((color) => isValidHexColor(color));
      setRecentColors(validColors.slice(0, maxRecentColors));
    } else {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored) as string[];
          // Validar y normalizar colores guardados
          const validColors = parsed
            .map((color) => normalizeHexColor(color))
            .filter((color) => isValidHexColor(color));
          setRecentColors(validColors.slice(0, maxRecentColors));
        }
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.error('Error loading recent colors:', error);
        }
      }
    }
  }, [initialRecentColors, maxRecentColors]);

  // Guardar colores recientes en localStorage
  const saveRecentColors = useCallback((colors: string[]) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(colors));
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('Error saving recent colors:', error);
      }
    }
  }, []);

  // Función interna para agregar color (sin debounce)
  const addColorInternal = useCallback(
    (color: string) => {
      const normalized = normalizeHexColor(color);
      if (!isValidHexColor(normalized)) return;

      setRecentColors((prev) => {
        // Remover el color si ya existe y agregarlo al inicio
        const updated = [normalized, ...prev.filter((c) => c !== normalized)].slice(
          0,
          maxRecentColors
        );
        saveRecentColors(updated);
        return updated;
      });
      pendingColorRef.current = null;
    },
    [maxRecentColors, saveRecentColors]
  );

  // Versión con debounce para usar durante el arrastre
  const addRecentColorDebouncedRef = useRef<ReturnType<typeof debounce> | null>(null);

  // Inicializar el debounce una vez
  useEffect(() => {
    addRecentColorDebouncedRef.current = debounce((...args: unknown[]) => {
      const color = args[0] as string;
      if (color) {
        addColorInternal(color);
      }
    }, debounceMs);

    return () => {
      // Cleanup: cancelar cualquier debounce pendiente al desmontar
      if (addRecentColorDebouncedRef.current) {
        // El debounce se limpia automáticamente, pero podemos limpiar la ref
        addRecentColorDebouncedRef.current = null;
      }
    };
  }, [debounceMs, addColorInternal]);

  // Agregar un color a los recientes (con debounce)
  const addRecentColor = useCallback((color: string) => {
    const normalized = normalizeHexColor(color);
    if (!isValidHexColor(normalized)) return;

    // Guardar el color pendiente
    pendingColorRef.current = normalized;

    // Ejecutar el debounce con el color actual
    if (addRecentColorDebouncedRef.current) {
      addRecentColorDebouncedRef.current(normalized);
    }
  }, []);

  // Agregar un color inmediatamente (sin debounce) - útil para clicks directos
  const addRecentColorImmediate = useCallback(
    (color: string) => {
      addColorInternal(color);
    },
    [addColorInternal]
  );

  // Limpiar colores recientes
  const clearRecentColors = useCallback(() => {
    setRecentColors([]);
    pendingColorRef.current = null;
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('Error clearing recent colors:', error);
      }
    }
  }, []);

  return {
    recentColors,
    addRecentColor,
    addRecentColorImmediate,
    clearRecentColors,
  };
};
